<script setup lang="ts">
import { ref } from 'vue'
import { ExternalLink, X } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import qiandaoCard from '@/assets/qiandao-card.png'

const { t } = useI18n({
  useScope: 'local',
  messages: {
    'zh-CN': {
      advertisement: '广告',
      brand: '千岛',
      description: '冒险岛交易上千岛',
      open: '广告：冒险岛交易上千岛（在新标签页打开）',
      visit: '前往千岛',
      close: '关闭广告',
    },
    en: {
      advertisement: 'Ad',
      brand: 'Qiandao',
      description: 'MapleStory trading on Qiandao',
      open: 'Advertisement: MapleStory trading on Qiandao (opens in a new tab)',
      visit: 'Visit Qiandao',
      close: 'Close advertisement',
    },
  },
})
const imageFailed = ref(false)
const imageReady = ref(false)
const dismissed = ref(false)

async function revealImage(event: Event): Promise<void> {
  const image = event.target as HTMLImageElement
  // Decode before fading in so slow image decoding cannot produce a blank reveal.
  try { await image.decode() } catch { /* The load event already confirmed the image is available. */ }
  imageReady.value = true
}

function prepareDismiss(element: Element): void {
  const slot = element as HTMLElement
  const frame = slot.querySelector<HTMLElement>('.maple-partner-card-frame')
  slot.style.setProperty('--dismiss-height', `${slot.getBoundingClientRect().height}px`)
  slot.style.setProperty('--dismiss-gap', slot.parentElement ? getComputedStyle(slot.parentElement).rowGap : '0px')
  if (frame) frame.style.width = `${frame.getBoundingClientRect().width}px`
  if (slot.contains(document.activeElement)) {
    slot.parentElement?.querySelector<HTMLElement>('.sidebar-footer button[tabindex="0"]')?.focus({ preventScroll: true })
  }
  slot.setAttribute('inert', '')
}
</script>

<template>
  <Transition name="maple-partner-card-dismiss" :duration="500" @before-leave="prepareDismiss">
    <div v-if="!dismissed" class="maple-partner-card-slot">
      <div class="maple-partner-card-frame" :class="{ 'is-ready': imageReady || imageFailed }" :inert="(!imageReady && !imageFailed) || undefined">
        <a
          class="maple-partner-card"
          href="https://qiandao.com/c2c/spu?islandId=302126&utm=MXDHJ001"
          target="_blank"
          rel="sponsored noopener noreferrer"
          :aria-label="t('open')"
        >
          <span class="maple-partner-card-artwork">
            <img
              v-if="!imageFailed"
              :src="qiandaoCard"
              :alt="t('description')"
              width="1000"
              height="1000"
              loading="lazy"
              decoding="async"
              referrerpolicy="no-referrer"
              @load="revealImage"
              @error="imageFailed = true"
            />
            <span v-else class="maple-partner-card-fallback" aria-hidden="true">
              <ExternalLink />
              <strong>{{ t('brand') }}</strong>
              <span>{{ t('visit') }}</span>
            </span>
          </span>
          <span class="maple-partner-card-label" aria-hidden="true">{{ t('advertisement') }}</span>
        </a>
        <button class="maple-partner-card-close" type="button" :aria-label="t('close')" :title="t('close')" @click="dismissed = true">
          <X aria-hidden="true" />
        </button>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.maple-partner-card-slot {
  container-type: size;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  flex: 1 1 13.375rem;
  min-height: 0;
  max-height: 13.375rem;
  margin-top: auto;
  margin-bottom: -0.5rem;
  transition: max-height 360ms cubic-bezier(0.2, 0.8, 0.2, 1);
}
.maple-partner-card-frame {
  position: relative;
  flex: none;
  width: min(100cqw, 100cqh);
  aspect-ratio: 1;
  opacity: 0;
  transition: opacity 420ms ease-in-out;
}
.maple-partner-card-frame.is-ready { opacity: 1; }
.maple-partner-card {
  position: relative;
  display: block;
  flex: none;
  height: 100%;
  width: 100%;
  max-width: 100%;
  aspect-ratio: 1;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 0.75rem;
  background: var(--surface);
  color: var(--muted-strong);
  text-decoration: none;
  box-shadow: var(--shadow-sm);
  transform: perspective(40rem) translateY(0) rotate(0) scale(1);
  transition: transform 420ms cubic-bezier(0.2, 0.8, 0.2, 1), border-color 300ms ease, box-shadow 420ms ease;
}
.maple-partner-card::after {
  content: '';
  position: absolute;
  z-index: 1;
  inset: -35% -70%;
  background: linear-gradient(110deg, transparent 38%, rgb(255 255 255 / 8%) 44%, rgb(255 255 255 / 48%) 50%, rgb(255 245 211 / 14%) 56%, transparent 62%);
  opacity: 0;
  transform: translateX(-65%);
  pointer-events: none;
}
.maple-partner-card-label { position: absolute; z-index: 2; right: 0.3125rem; bottom: 0.3125rem; padding: 0.125rem 0.3125rem; border: 1px solid rgb(255 255 255 / 22%); border-radius: 0.25rem; background: rgb(0 0 0 / 72%); color: #fff; font-size: 0.625rem; line-height: 1.2; pointer-events: none; }
.maple-partner-card-artwork { display: block; height: 100%; background: var(--surface-subtle); }
.maple-partner-card-artwork img { display: block; width: 100%; height: 100%; object-fit: contain; transition: transform 550ms cubic-bezier(0.2, 0.8, 0.2, 1); }
.maple-partner-card:focus-visible { outline: 2px solid var(--primary) !important; outline-offset: 3px; }
.maple-partner-card-fallback { height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.5rem; color: var(--muted-strong); font-size: var(--font-caption); }
.maple-partner-card-fallback > svg { width: 1.5rem; height: 1.5rem; color: var(--primary); }
.maple-partner-card-fallback > strong { color: var(--text-strong); font-size: var(--font-heading); }
.maple-partner-card-close {
  position: absolute;
  z-index: 3;
  top: 0.25rem;
  right: 0.25rem;
  display: grid;
  place-items: center;
  width: 1.25rem;
  height: 1.25rem;
  padding: 0;
  border: 1px solid rgb(255 255 255 / 35%);
  border-radius: 50%;
  background: rgb(0 0 0 / 65%);
  color: #fff;
  cursor: pointer;
  transition: background-color 180ms ease, transform 180ms ease;
}
.maple-partner-card-close::before { content: ''; position: absolute; inset: -0.125rem; border-radius: 50%; }
.maple-partner-card-close svg { width: 0.75rem; height: 0.75rem; }
.maple-partner-card-close:hover { background: rgb(0 0 0 / 88%); transform: scale(1.1); }
.maple-partner-card-close:focus-visible { outline: 2px solid var(--primary) !important; outline-offset: 2px; }
@media (max-width: 70rem) {
  .maple-partner-card-slot { max-height: 3.625rem; }
  .maple-partner-card { border-radius: 0.5rem; }
  .maple-partner-card-close { top: -0.375rem; right: -0.375rem; }
  .maple-partner-card-label { right: 0.125rem; bottom: 0.125rem; padding: 0.0625rem 0.125rem; font-size: 0.5625rem; }
  .maple-partner-card-fallback { gap: 0.125rem; }
  .maple-partner-card-fallback > svg, .maple-partner-card-fallback > span { display: none; }
  .maple-partner-card-fallback > strong { font-size: 0.625rem; }
}
@media (max-width: 47.5rem) { .maple-partner-card-slot { display: none; } }
/* Fixed timings also preserve dismissal motion when reduced motion is enabled. */
.maple-partner-card-dismiss-leave-active {
  max-height: var(--dismiss-height);
  flex-basis: var(--dismiss-height);
  pointer-events: none;
  transition: max-height 220ms ease-in-out 280ms,
    flex-basis 220ms ease-in-out 280ms,
    margin-bottom 220ms ease-in-out 280ms;
}
.maple-partner-card-dismiss-leave-active .maple-partner-card-frame {
  transition: opacity 280ms ease-in-out;
}
.maple-partner-card-dismiss-leave-to {
  max-height: 0;
  flex-basis: 0;
  margin-bottom: calc(-1 * var(--dismiss-gap));
}
.maple-partner-card-dismiss-leave-to .maple-partner-card-frame { opacity: 0; }
/* This user-triggered effect intentionally stays enabled with reduced motion. */
@media (hover: hover) and (pointer: fine) {
  .maple-partner-card:is(:hover, :focus-visible) {
    transform: perspective(40rem) translateY(-3px) rotate(-0.7deg) scale(1.025);
    border-color: color-mix(in srgb, var(--amber) 72%, var(--border));
    box-shadow: 0 0.625rem 1.5rem rgb(0 0 0 / 20%), 0 0 1.375rem color-mix(in srgb, var(--amber) 24%, transparent);
  }
  .maple-partner-card:is(:hover, :focus-visible) .maple-partner-card-artwork img { transform: scale(1.025); }
  .maple-partner-card:is(:hover, :focus-visible)::after { animation: maple-partner-card-shine 850ms cubic-bezier(0.2, 0.6, 0.3, 1) both; }
  .maple-partner-card:active { transform: perspective(40rem) translateY(-1px) rotate(0) scale(0.99); transition-duration: 120ms; }
}
@keyframes maple-partner-card-shine {
  0% { opacity: 0; transform: translateX(-65%); }
  18% { opacity: 1; }
  80% { opacity: 0.8; }
  100% { opacity: 0; transform: translateX(65%); }
}
</style>
